import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FiPhone, FiMail, FiMapPin, FiArrowUpRight, FiSend, FiChevronRight } from "react-icons/fi";
import { Helmet } from "react-helmet-async";

const scrollReveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: "easeOut" },
};

const CONTACT_INFO = [
  {
    icon: <FiPhone />,
    label: "Phone",
    value: "+91 98714 23482",
    href: "tel:+919871423482",
  },
  {
    icon: <FiMail />,
    label: "Email",
    value: "officialpdmarketz@gmail.com",
    href: "mailto:officialpdmarketz@gmail.com",
  },
  {
    icon: <FiMapPin />,
    label: "Location",
    value: "New Delhi, India",
    href: null,
  },
];

function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create email content
    const emailSubject = encodeURIComponent("Contact Form Submission - PD Marketz");
    const emailBody = encodeURIComponent(
      `Name: ${form.name}\n` +
      `Email: ${form.email}\n` +
      `Phone: ${form.phone}\n` +
      `Service: ${form.service}\n` +
      `Message: ${form.message}\n\n` +
      `---\n` +
      `This contact form was submitted from the PD Marketz website.`
    );
    
    // Open email client
    window.location.href = `mailto:officialpdmarketz@gmail.com?subject=${emailSubject}&body=${emailBody}`;
    
    setSubmitted(true);
  };

  return (
    <>
      <Helmet>
        <title>Contact Digital Marketing Company | Get in Touch with Experts</title>
        <meta name="description" content="Contact our digital marketing experts for SEO, PPC, social media, and lead generation services. Get in touch today to grow your business." />
      </Helmet>
      <main className="contact-page">
      {/* HERO */}
      <section className="about-page-hero">
        <div className="about-page-hero-inner">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Contact Us
          </motion.h1>
          <motion.p
            className="service-hero-sub"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Have a question or want to grow your business online? We're here to help.
          </motion.p>
          <motion.p
            className="service-hero-breadcrumb"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Link to="/" style={{ color: "#5b4cff", textDecoration: "none" }}>Home</Link>
            <FiChevronRight style={{ margin: "0 4px", fontSize: "12px" }} />
            <span>Contact Us</span>
          </motion.p>
        </div>
      </section>

      {/* CONTACT INFO + FORM */}
      <section className="contact-main py-20">
        <div className="container-custom">
          <div className="contact-grid">

            {/* LEFT — Info */}
            <motion.div className="contact-info-col" {...scrollReveal}>
              <span className="service-detail-label" style={{ display: "inline-block" }}>Get in Touch</span>
              <h2 className="contact-info-heading">Let's Talk About Your Business</h2>
              <p className="contact-info-desc">
                Feel free to reach out to us for any queries, project discussions, or digital marketing solutions.
                Our team is here to help you with the right strategy for your business.
              </p>

              <div className="contact-cards">
                {CONTACT_INFO.map((item) => (
                  <div className="contact-card" key={item.label}>
                    <div className="contact-card-icon">{item.icon}</div>
                    <div>
                      <span className="contact-card-label">{item.label}</span>
                      {item.href ? (
                        <a href={item.href} className="contact-card-value">{item.value}</a>
                      ) : (
                        <p className="contact-card-value">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* RIGHT — Form */}
            <motion.div
              className="contact-form-col"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            >
              <div className="contact-form-card">
                <h3 className="contact-form-title">Send Us a Message</h3>
                <p className="contact-form-sub">Tell us your requirements and we'll get back to you shortly.</p>

                {submitted ? (
                  <div className="contact-success">
                    <span>✓</span>
                    <p>Thank you! We'll get back to you within 24 hours.</p>
                  </div>
                ) : (
                  <form className="contact-form" onSubmit={handleSubmit}>
                    <div className="contact-form-row">
                      <div className="contact-form-group">
                        <label htmlFor="name">Full Name *</label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          placeholder="Your name"
                          value={form.name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="contact-form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="your@email.com"
                          value={form.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="contact-form-row">
                      <div className="contact-form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="+91 XXXXX XXXXX"
                          value={form.phone}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="contact-form-group">
                        <label htmlFor="service">Service Interested In</label>
                        <select id="service" name="service" value={form.service} onChange={handleChange}>
                          <option value="">Select a service</option>
                          <option>SEO Services</option>
                          <option>PPC Advertising</option>
                          <option>Social Media Marketing</option>
                          <option>Content Marketing</option>
                          <option>Email Marketing</option>
                          <option>Web Design & Development</option>
                          <option>Ecommerce Services</option>
                        </select>
                      </div>
                    </div>
                    <div className="contact-form-group">
                      <label htmlFor="message">Your Message *</label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        placeholder="Tell us about your project or requirements…"
                        value={form.message}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <button type="submit" className="contact-submit-btn">
                      Send Message
                      <FiSend />
                    </button>
                  </form>
                )}
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="service-page-cta py-20">
        <div className="container-custom">
          <motion.div
            className="service-page-cta-inner text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Ready to Grow Your Business Online?</h2>
            <p className="mb-8">
              Let's discuss your goals and create the right strategy for your growth.
            </p>
            <a href={`mailto:officialpdmarketz@gmail.com?subject=${encodeURIComponent("Free Consultation Request — PD Marketz")}&body=${encodeURIComponent("Hi PD Marketz Team,\n\nI would like to request a free consultation.\n\nThank you.")}`} className="contact-cta-btn">
              Get Free Consultation
              <span className="nav-btn-arrow" style={{ width: "28px", height: "28px", background: "#5b47fb", color: "#fff" }}>
                <FiArrowUpRight />
              </span>
            </a>
          </motion.div>
        </div>
      </section>
      </main>
    </>
  );
}

export default ContactPage;
